import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, ArrowLeft, ArrowRight, KeyRound, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SentinelStage } from "@/components/fx/SentinelStage";

export default function ForgotPassword() {
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [questions, setQuestions] = useState([]);
    const [resetToken, setResetToken] = useState("");
    const [answers, setAnswers] = useState(["", "", ""]);
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const start = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password/start", { email: email.trim().toLowerCase() });
            setQuestions(data.questions);
            setResetToken(data.reset_token);
            setStep(2);
        } catch (err) {
            setError(err?.response?.data?.detail || "Unable to initiate recovery");
        } finally { setLoading(false); }
    };

    const complete = async (e) => {
        e.preventDefault();
        setError("");
        if (newPassword !== confirm) return setError("Passwords do not match");
        if (newPassword.length < 8) return setError("Password must be at least 8 characters");
        setLoading(true);
        try {
            await api.post("/auth/forgot-password/complete", {
                reset_token: resetToken,
                answers: questions.map((q, i) => ({ question: q, answer: answers[i] })),
                new_password: newPassword,
            });
            toast.success("Password reset", { description: "You can now sign in with your new password." });
            setStep(3);
            setTimeout(() => nav("/login"), 1500);
        } catch (err) {
            setError(err?.response?.data?.detail || "Recovery failed");
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden" data-testid="forgot-password-page">
            <SentinelStage testId="forgot-sentinel-stage" />
            <div className="sentinel-auth-frame rounded-[6px] p-10 max-w-md w-full relative z-10">
                <Link to="/login" className="flex items-center gap-3 mb-8 hover:opacity-90">
                    <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                        <Shield size={20} className="text-slate-950" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-display text-lg text-white">UTSAVA <span className="text-cyan-400">Genesis</span></div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Account Recovery</div>
                    </div>
                </Link>

                {step === 1 && (
                    <form onSubmit={start} className="space-y-4">
                        <div className="label-eyebrow flex items-center gap-1.5"><KeyRound size={11} /> Step 01 of 02</div>
                        <h1 className="font-display text-2xl text-white">Forgot your password?</h1>
                        <p className="text-sm text-slate-400">Enter the email on your account. We will ask the security questions you registered with.</p>
                        <div>
                            <label className="label-eyebrow block mb-2">Email</label>
                            <input
                                data-testid="forgot-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="field"
                                placeholder="you@company.com"
                                required
                                autoFocus
                            />
                        </div>
                        {error && (
                            <div className="flex items-start gap-2 p-3 border border-rose-500/30 bg-rose-500/5 text-rose-300 text-xs rounded-sm">
                                <AlertCircle size={14} className="mt-0.5" /><span>{error}</span>
                            </div>
                        )}
                        <button data-testid="forgot-next" type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                            {loading ? "Verifying…" : "Continue"}{!loading && <ArrowRight size={14} />}
                        </button>
                        <Link to="/login" className="btn-ghost w-full flex items-center justify-center gap-2"><ArrowLeft size={12} /> Back to sign-in</Link>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={complete} className="space-y-4">
                        <div className="label-eyebrow flex items-center gap-1.5"><KeyRound size={11} /> Step 02 of 02</div>
                        <h1 className="font-display text-2xl text-white">Verify your identity</h1>
                        <p className="text-sm text-slate-400">Answer the security questions and set a new password.</p>
                        {questions.map((q, i) => (
                            <div key={q}>
                                <label className="label-eyebrow block mb-2">{q}</label>
                                <input
                                    data-testid={`forgot-answer-${i + 1}`}
                                    value={answers[i]}
                                    onChange={(e) => {
                                        const next = [...answers]; next[i] = e.target.value; setAnswers(next);
                                    }}
                                    className="field"
                                    placeholder="Your answer"
                                    required
                                />
                            </div>
                        ))}
                        <div className="pt-2 divider-glow" />
                        <div>
                            <label className="label-eyebrow block mb-2">New Password</label>
                            <input
                                data-testid="forgot-new-password"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="field"
                                required
                                minLength={8}
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-2">Confirm Password</label>
                            <input
                                data-testid="forgot-confirm-password"
                                type="password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="field"
                                required
                            />
                        </div>
                        {error && (
                            <div className="flex items-start gap-2 p-3 border border-rose-500/30 bg-rose-500/5 text-rose-300 text-xs rounded-sm">
                                <AlertCircle size={14} className="mt-0.5" /><span>{error}</span>
                            </div>
                        )}
                        <button data-testid="forgot-submit" type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                            {loading ? "Resetting…" : "Reset Password"}{!loading && <ArrowRight size={14} />}
                        </button>
                    </form>
                )}

                {step === 3 && (
                    <div className="text-center py-8">
                        <CheckCircle2 size={42} className="text-emerald-400 mx-auto mb-4" />
                        <h2 className="font-display text-2xl text-white">Password reset</h2>
                        <p className="text-sm text-slate-400 mt-2">Redirecting to sign-in…</p>
                    </div>
                )}
            </div>
        </div>
    );
}
